import { useEffect, useState } from 'react';
import { api, type Template } from '../api-client';
import { tenantStore } from '../lib/tenant-store';

const SAMPLE_YAML = `id: yayoi-hanbai-export
name: 弥生販売 受注インポート
source:
  cart: bcart-mock
  entity: orders
connector: yayoi-hanbai
renderer: csv
delivery: email
delivery_config:
  to: ["example@example.com"]
  subject: "弥生販売 受注データ"
schedule:
  cron: "0 9 * * *"
`;

export default function Templates() {
  const tenantId = tenantStore.get() ?? '';
  const [templates, setTemplates] = useState<Template[]>([]);
  const [name, setName] = useState('弥生販売 受注インポート');
  const [yaml, setYaml] = useState(SAMPLE_YAML);
  const [running, setRunning] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (tenantId) api.listTemplates(tenantId).then((r) => setTemplates(r.templates)).catch(() => {});
  }, [tenantId]);

  if (!tenantId) return <p>先にテナントを設定してください</p>;

  async function reload() {
    const r = await api.listTemplates(tenantId);
    setTemplates(r.templates);
  }

  async function handleCreate() {
    try {
      await api.createTemplate({ tenantId, name, yamlContent: yaml });
      setMessage('✓ テンプレ作成完了');
      await reload();
    } catch (e) {
      setMessage(`✗ ${(e as Error).message}`);
    }
  }

  async function handleRun(t: Template) {
    setRunning(t.id);
    try {
      const r = await api.runTemplate({ tenantId, templateId: t.id });
      setMessage(`✓ ジョブ実行: ${r.jobId.slice(0, 8)} (${r.rowCount}行)`);
    } catch (e) {
      setMessage(`✗ ${(e as Error).message}`);
    } finally {
      setRunning(null);
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">テンプレート</h2>

      <div className="bg-white rounded shadow border p-4 mb-6">
        <h3 className="font-semibold mb-2">既存テンプレ ({templates.length})</h3>
        {templates.length === 0 ? (
          <p className="text-slate-500 text-sm">テンプレがありません</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-3 py-2">名前</th>
                <th className="px-3 py-2">版数</th>
                <th className="px-3 py-2">更新</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-3 py-2">{t.name}</td>
                  <td className="px-3 py-2">v{t.version}</td>
                  <td className="px-3 py-2 text-slate-500">{new Date(t.updated_at).toLocaleString('ja-JP')}</td>
                  <td className="px-3 py-2">
                    <button
                      disabled={running === t.id}
                      onClick={() => handleRun(t)}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {running === t.id ? '実行中…' : '今すぐ実行'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded shadow border p-4">
        <h3 className="font-semibold mb-2">新規テンプレ</h3>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="名前"
          className="w-full px-3 py-2 border rounded mb-2"
        />
        <textarea
          value={yaml}
          onChange={(e) => setYaml(e.target.value)}
          rows={14}
          className="w-full px-3 py-2 border rounded font-mono text-xs"
        />
        <div className="mt-2 flex gap-2 items-center">
          <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            作成
          </button>
          {message && <p className="text-sm">{message}</p>}
        </div>
      </div>
    </div>
  );
}
